import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import LandingPage from "./LandingPage/LandingPage";
import AppPage from "./Application/AppPage";
import SignUp from "./Auth/SignUp";
import Login from "./Auth/Login";
import ConnectorPage from "./ConnectorPage/ConnectorPage";
import APIHelper from "./LandingPage/utils/APIHelper";
import VerifyEmail from "./Auth/VerifyEmail";
import ForgotPassword from "./Auth/ForgotPassword";
import ResetPassword from "./Auth/ResetPassword";
import PricingPage from "./LandingPage/PricingPage";
import PaymentSuccessPage from "./Application/pages/PaymentSuccessPage";
import CheckoutPage from "./Application/pages/CheckoutPage";

function ProtectedRoute({ children, token }) {
  return token ? children : <Navigate to="/" replace />;
}

function getQueryParams() {
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [hasAccessToken, setHasAccessToken] = useState(null); // null -> loading state
  const [oauthError, setOauthError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleAuthSuccess = (token) => {
    setToken(token);
    localStorage.setItem("token", token);
    checkAccessToken(token);
  };

  const checkAccessToken = (token) => {
    try {
      const decodedToken = jwtDecode(token);

      if (decodedToken.exp) {
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        if (decodedToken.exp > currentTime) {
          setHasAccessToken(Boolean(decodedToken.accessToken)); // Check if accessToken exists in payload
        } else {
          console.warn("Token has expired. Logging out...");
          logout(); // Expired token
        }
      } else {
        console.error("Token missing 'exp' claim. Logging out...");
        logout(); // Invalid token
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      logout(); // Invalid token
    }
  };

  const logout = () => {
    localStorage.removeItem("token"); // Remove the token from localStorage
    setToken(null); // Clear token in state
    setHasAccessToken(false);
    navigate("/"); // Redirect to root page using navigate
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      checkAccessToken(storedToken);
      fetchLatestToken(storedToken);
    } else {
      setHasAccessToken(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const fetchLatestToken = async (currentToken) => {
    try {
      const response = await APIHelper.getToken(currentToken);
      const updatedToken = response.token;
      setToken(updatedToken);
      localStorage.setItem("token", updatedToken);

      const decodedToken = jwtDecode(updatedToken);
      setHasAccessToken(Boolean(decodedToken.accessToken));

      checkAccessToken(updatedToken);
    } catch (error) {
      console.error("Error fetching latest token:", error);
    }
  };

  useEffect(() => {
    if (hasAccessToken && location.pathname === "/app") {
      navigate("/dashboard"); // Ensure navigation to /dashboard
    }
  }, [hasAccessToken, location.pathname, navigate]);

  useEffect(() => {
    // Check for OAuth errors in the URL
    const params = getQueryParams();
    if (params.success === "false" && params.error) {
      let message = "Facebook authentication failed.";
      if (params.error === "missing_code") {
        message = "Facebook did not return an access code. Please try again.";
      } else if (params.error === "access_denied") {
        message = "You denied access. Please allow permissions to continue.";
      } else if (params.error === "server_error") {
        message = "A server error occurred. Please try again.";
      } else if (params.desc) {
        message = decodeURIComponent(params.desc);
      }
      setOauthError(message);

      // Optionally, clean up the URL so the error doesn't persist on refresh
      navigate(location.pathname, { replace: true });
    }
  }, []);

  if (hasAccessToken === null) {
    // Show loading only during the initial check; not on invalid or expired tokens
    return <div>Loading...</div>;
  }

  return (
    <div className="App">
      {oauthError && (
        <div style={{ color: "red", margin: "1em" }}>{oauthError}</div>
      )}
      <Routes>
        <Route
          path="/"
          element={
            token ? (
              hasAccessToken ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/app" replace />
              )
            ) : (
              <LandingPage onAuthSuccess={handleAuthSuccess} />
            )
          }
        />
        {/* Add PricingPage route for all users */}
        <Route path="/pricing" element={<PricingPage standalone={true} />} />

        {/* Public checkout page for end customers */}
        <Route path="/checkout/:productId" element={<CheckoutPage />} />

        {/* Payment success page - public route */}
        <Route
          path="/payment-success"
          element={<PaymentSuccessPage token={token} />}
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute token={token}>
              {!token || (token && !hasAccessToken) ? (
                <ConnectorPage
                  token={token}
                  onAccessTokenUpdated={() => setHasAccessToken(true)} // Trigger state update when accessToken is added
                />
              ) : (
                <Navigate to="/dashboard" replace />
              )}
            </ProtectedRoute>
          }
        />
        {/* Redirect /app/comments/configure to /dashboard/comments/configure */}
        <Route
          path="/app/comments/configure"
          element={
            <ProtectedRoute token={token}>
              {hasAccessToken ? (
                <Navigate
                  to={`/dashboard/comments/configure${window.location.search}`}
                  replace
                />
              ) : (
                <Navigate to="/app" replace />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute token={token}>
              {hasAccessToken ? (
                <AppPage token={token} />
              ) : (
                <Navigate to="/app" replace />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup"
          element={<SignUp onAuthSuccess={handleAuthSuccess} />}
        />
        <Route
          path="/login"
          element={<Login onAuthSuccess={handleAuthSuccess} />}
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    </div>
  );
}

export default App;
